import { z } from 'zod'
import { oc } from '@orpc/contract'
import {
  languagesSchema,
  licenseRowSchema,
  professionalRoleSchema,
  specialtySchema,
} from './member-credentials.contract'
import { doseSpotClinicianKindSchema } from './practitioner-dosespot-clinician.contract'

export const practitionerRoleCodeSchema = z.object({
  code: z.string().optional(),
  display: z.string().optional(),
})

// One Org Member of an Organization with their Credentials at a glance — the row of the
// Provider Operations list and of Super → Organizations → Open Members.
export const practitionerRowSchema = z.object({
  practitionerId: z.string(),
  practitionerRoleId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  displayName: z.string(),
  email: z.string().optional(),
  roles: z.array(practitionerRoleCodeSchema),
  // Credential fields: the NPI, the Professional Role and Specialty (`null` when none; a
  // practitioner saved before they existed reads those of their first Active license) and
  // the per-state licenses.
  npiNumber: z.string().optional(),
  professionalRole: professionalRoleSchema.nullable(),
  specialty: specialtySchema.nullable(),
  // The Languages, empty when none is recorded; a stored code outside the list is dropped.
  languages: languagesSchema,
  licenses: z.array(licenseRowSchema),
  // The Active Provider status (see the glossary); a member with nothing recorded is active.
  activeProvider: z.boolean(),
  // The DoseSpot Clinician Mapping — present means this member may launch DoseSpot (§5.1).
  // Written by `setPractitionerDoseSpotClinician`, not by the credentials save. The kind
  // (PD-1592) is present exactly when the id is; a legacy mapping reads as `prescriber`.
  doseSpotClinicianId: z.string().optional(),
  doseSpotClinicianKind: doseSpotClinicianKindSchema.optional(),
  // The Bridge provider mapping — present means Bridge can name this member in an
  // eligibility answer. Written by `setPractitionerBridgeProvider`, not by the
  // credentials save.
  bridgeProviderId: z.string().optional(),
  // The Stedi provider-record mapping — same shape and writer discipline as Bridge's,
  // written by `setPractitionerStediProvider` only.
  stediProviderId: z.string().optional(),
})

export type PractitionerRowDto = z.infer<typeof practitionerRowSchema>

export const PRACTITIONER_ROLE_FILTERS = ['practitioner', 'care_coordinator'] as const

// Mirrors `ModelPageRequest` / `ModelPage` (src/model/types.ts; canonical: `listPatients`).
const pageRequestSchema = z.object({
  offset: z.number().int().min(0),
  count: z.number().int().min(0),
  limit: z.number().int().min(0),
})

/**
 * The Org Members of one Organization, one row per member. The Organization must be in the
 * caller's Provider Operations scope (`getMyPractitionerOpsScope`), else 403 — a Care
 * Coordinator asking for another Organization's list is refused here, not only hidden in
 * the UI.
 */
export const listPractitioners = oc
  .route({ method: 'POST', path: '/v2/practitioners/list' })
  .input(
    z.object({
      organizationId: z.string().min(1),
      page: pageRequestSchema,
      // Name tokens, or an exact email when the text contains "@".
      search: z.string().trim().max(200).optional(),
      // Server-side filter by clinical role. Absent = all roles.
      role: z.enum(PRACTITIONER_ROLE_FILTERS).optional(),
      // Server-side sort by member name. Absent = default (insertion) order.
      order: z.array(z.object({ name: z.enum(['asc', 'desc']) })).optional(),
    }),
  )
  .output(
    z.object({
      page: pageRequestSchema.extend({
        size: z.number().int().min(0),
        total: z.number().int().min(0),
      }),
      data: z.array(practitionerRowSchema),
    }),
  )
